// The service worker (vite-plugin-pwa, generateSW, dist/sw.js): precaches the built shell only; /api is never cached
// and never gets the navigation fallback (vite.config.ts). The first install controls the page at once
// (clientsClaim); a later version waits until the person chooses Reload ("Update ready"), which sends workbox's
// SKIP_WAITING message to the version waiting at that moment. Registered by hand: workbox-window (behind
// virtual:pwa-register) is not a dependency.
//
// Plan section E, "Data on the device": every API answer carries the build id (X-NW-Build, lib/build.ts). An older
// client shows "Update ready" and reloads at the next navigation; an incompatible contract forces the reload.
//   - an answer from another release makes the browser look for the new worker (registration.update()); once it is
//     installed and waiting, the offer shows (Frame.tsx) and the next navigation reloads into it (router.tsx);
//   - an answer of another release that this client cannot read (lib/api.ts, lib/session.ts) activates the new version
//     and reloads at once, at most once per release in a tab (a release the browser cannot find would loop otherwise);
//     only these two count, error answers and screen failures get the offer (docs/DECISIONS.md, FX-INCOMPAT-SIGNAL);
//   - with no worker on this device (blocked, private mode) a plain reload fetches the new files, so it is offered too.
// Sign-out and a stale session answer Clear-Site-Data: "cache", "storage" (CONTRACTS.md section 7), which also removes
// the worker and its caches; the next successful session check registers it again (ensureWorker, lib/session.ts).
import { useSyncExternalStore } from 'react'
import { watchBuild } from './build.ts'

const FORCED_KEY = 'nw:forced-reload'

let ready = false
const listeners = new Set<() => void>()

/** An update is waiting (or, with no worker, a reload is due). Idempotent: the offer exists once per page. */
function markReady() {
  if (ready) return
  ready = true
  listeners.forEach((l) => l())
}

async function currentRegistration(): Promise<ServiceWorkerRegistration | undefined> {
  if (!('serviceWorker' in navigator)) return undefined
  try {
    return await navigator.serviceWorker.getRegistration('/')
  } catch {
    return undefined
  }
}

/**
 * Activates the version waiting at this moment and reloads once it controls the page; reloads at once when none
 * waits. Read at the call, never kept from the offer: a newer version may have replaced the one offered, or another
 * window may have activated it already.
 */
export async function activateUpdate(): Promise<void> {
  const waiting = (await currentRegistration())?.waiting
  if (!waiting) return window.location.reload()
  navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), { once: true })
  waiting.postMessage({ type: 'SKIP_WAITING' })
}

const reload = () => void activateUpdate()

/**
 * Called by the router before it loads a new path (router.tsx): an update that is ready replaces this page there. The
 * returned promise never settles, so that navigation never loads: no screen mounts and no request leaves the page
 * until the reload. Measured (F-NAV-RELOAD-STALL): Chrome stops the old worker when SKIP_WAITING arrives, and a request
 * made in that moment (the next screen's session check, the event stream of a frame that mounts again) starts it again;
 * the new version then stays 'installed' until Chrome's five-minute limit on the old one. The browser's own request
 * after the address changes, the favicon, bypasses the worker instead (public/sw-routes.js).
 */
export function reloadIfUpdateReady(): Promise<never> | undefined {
  if (!ready) return undefined
  reload()
  return new Promise<never>(() => {})
}

function settled(worker: ServiceWorker): Promise<void> {
  return new Promise((resolve) => {
    const done = () => {
      if (worker.state === 'installing') return
      worker.removeEventListener('statechange', done)
      resolve()
    }
    worker.addEventListener('statechange', done)
    done()
  })
}

/** An answer of another release this client cannot read: get the new version in and reload, once per release. */
async function forceUpdate(build: string): Promise<void> {
  try {
    if (sessionStorage.getItem(FORCED_KEY) === build) return
    sessionStorage.setItem(FORCED_KEY, build)
  } catch {
    return // no session storage: a reload could not be told from a loop
  }
  const reg = await currentRegistration()
  if (reg) {
    await reg.update().catch(() => {})
    if (reg.installing) await settled(reg.installing)
  }
  await activateUpdate()
}

/** An answer from another release: look for the new worker; with none on this device, a reload is the update. */
async function lookForUpdate(): Promise<void> {
  const reg = await currentRegistration()
  if (!reg) return markReady()
  await reg.update().catch(() => {})
}

const tracked = new WeakSet<ServiceWorkerRegistration>()

function track(reg: ServiceWorkerRegistration) {
  if (tracked.has(reg)) return
  tracked.add(reg)
  if (reg.waiting && navigator.serviceWorker.controller) markReady()
  reg.addEventListener('updatefound', () => {
    const next = reg.installing
    next?.addEventListener('statechange', () => {
      if (next.state === 'installed' && navigator.serviceWorker.controller) markReady()
    })
  })
}

/** Registers /sw.js (production builds only; the dev server serves no worker). Registering again is a no-op. */
export function ensureWorker(): void {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return
  navigator.serviceWorker.register('/sw.js', { scope: '/' }).then(
    (reg: ServiceWorkerRegistration | undefined) => {
      if (reg) track(reg) // a browser that blocks workers (automation) resolves without a registration
    },
    () => {
      /* no worker (private mode, blocked): the app still runs online */
    },
  )
}

/** Registers /sw.js in production builds (the dev server serves no worker) and starts listening for newer releases. */
export function startServiceWorker(): void {
  watchBuild(({ build, incompatible }) => void (incompatible ? forceUpdate(build) : lookForUpdate()))
  window.addEventListener('load', ensureWorker)
}

/** A function that activates the waiting version and reloads, once one is ready; null before that. */
export function useUpdateReady(): (() => void) | null {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l)
      return () => listeners.delete(l)
    },
    () => (ready ? reload : null),
  )
}
